import React, { useRef, useState } from "react";
import { PortfolioItem } from "@/hooks/useProfile";
import EmptyState from "./EmptyState";
import AddPortfolioItemForm from "./portfolio/AddPortfolioItemForm";
import { usePortfolioSection } from "./portfolio/usePortfolioSection";
import PortfolioHeader from "./portfolio/components/PortfolioHeader";
import PortfolioItemsList from "./portfolio/components/PortfolioItemsList";
import CombinedPortfolioList from "./portfolio/CombinedPortfolioList";
import EditPortfolioItemForm from "./portfolio/EditPortfolioItemForm";
import { normalizePortfolioItems } from "@/hooks/portfolio/usePortfolioNormalizer";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Home, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import PortfolioEmptyState, {
  PortfolioEmptyStateHandle,
} from "./portfolio/components/PortfolioEmptyState";
import ImageAddingSection from "./portfolio/components/images/ImageAddingSection";
import { v4 as uuidv4 } from "uuid";
// Tabs removed to show a combined grid
import { CaseStudyList } from "./portfolio/case-study/CaseStudyList";
import { CaseStudyForm } from "./portfolio/case-study/CaseStudyForm";
import { CaseStudyCreationModal } from "./portfolio/case-study/CaseStudyCreationModal";
import { ShotCreationModal } from "./portfolio/ShotCreationModal";
import { CaseStudyItem } from "@/types/case-study";
import { useCaseStudies } from "@/hooks/useCaseStudy";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { useProfileContext } from "@/contexts/ProfileContext";
import { useUnifiedPortfolio } from "@/hooks/portfolio/useUnifiedPortfolio";
import { useUnifiedPortfolioQuery } from "@/hooks/portfolio/useUnifiedPortfolioQuery";
import { UnifiedPortfolioItem, UnifiedPortfolioService } from "@/services/supabase/unifiedPortfolio";
import PortfolioLayoutList from "./portfolio/PortfolioLayoutList";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface PortfolioSectionProps {
  portfolioItems: PortfolioItem[] | null;
  onPortfolioChange: (portfolioItems: PortfolioItem[]) => void;
  activeSection?: string;
  userProfession?: string | null;
  userSkills?: string[] | null;
  isVerified?: boolean;
}

const PortfolioErrorFallback = () => (
  <div className="space-y-6">
    <h1 className="text-2xl font-bold mb-8">Portfolio</h1>
    <Alert variant="destructive" className="mb-8">
      <AlertCircle className="h-5 w-5" />
      <div>
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription className="mt-2 space-y-4">
          <p>We encountered an error while loading your portfolio.</p>
          <Button asChild>
            <Link to="/dashboard">
              <Home className="mr-2 h-4 w-4" /> Return to Dashboard
            </Link>
          </Button>
        </AlertDescription>
      </div>
    </Alert>
  </div>
);

const PortfolioSection = ({
  portfolioItems = [],
  onPortfolioChange,
  activeSection = "portfolio",
  userProfession,
  userSkills,
  isVerified = false,
}: PortfolioSectionProps) => {
  const navigate = useNavigate();
  return (
    <ErrorBoundary fallback={<PortfolioErrorFallback />}>
      <PortfolioSectionContent
        portfolioItems={portfolioItems}
        onPortfolioChange={onPortfolioChange}
        activeSection={activeSection}
        userProfession={userProfession}
        userSkills={userSkills}
        isVerified={isVerified}
      />
    </ErrorBoundary>
  );
};

const PortfolioSectionContent = ({
  portfolioItems = [],
  onPortfolioChange,
  activeSection = "portfolio",
  userProfession,
  userSkills,
  isVerified = false,
}: PortfolioSectionProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfileContext();
  const queryClient = useQueryClient();
  const [showUploader, setShowUploader] = useState(false);
  const [isCreatingCaseStudy, setIsCreatingCaseStudy] = useState(false);
  const [editingCaseStudy, setEditingCaseStudy] = useState<CaseStudyItem | null>(null);
  const [showCaseStudyModal, setShowCaseStudyModal] = useState(false);
  const [showShotModal, setShowShotModal] = useState(false);
  const [importedShotData, setImportedShotData] = useState<Partial<PortfolioItem> | null>(null);
  const [shotSaveStatus, setShotSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  
  // Filter and sort state
  const [activeFilter, setActiveFilter] = useState<'all' | 'case_studies' | 'shots' | 'portfolio_layout'>('all');
  const [sortOrder, setSortOrder] = useState<'last_added' | 'alphabetical' | 'featured_first' | 'most_viewed'>('last_added');

  // Debug logging
  React.useEffect(() => {
    console.log('Active filter changed:', activeFilter);
  }, [activeFilter]);

  React.useEffect(() => {
    console.log('Sort order changed:', sortOrder);
  }, [sortOrder]);
  
  // Unified portfolio query
  const { data: unifiedItems = [], isLoading: unifiedLoading } = useUnifiedPortfolioQuery({
    profileId: profile?.id,
    includeDrafts: true, // Include drafts for dashboard editing
    enabled: !!profile?.id
  });
  // Portfolio items are now handled by unified query

  // Split unified items by type
  const unifiedShots = React.useMemo(() => {
    return unifiedItems.filter(item => item.item_type === 'shot');
  }, [unifiedItems]);

  const unifiedCaseStudies = React.useMemo(() => {
    return unifiedItems.filter(item => item.item_type === 'case_study');
  }, [unifiedItems]);

  // Count items for display
  const allCount = unifiedItems.length;
  const caseStudiesCount = unifiedCaseStudies.length;
  const shotsCount = unifiedShots.length;


  // Filter items based on active filter
  const filteredItems = React.useMemo(() => {
    switch (activeFilter) {
      case 'case_studies':
        return unifiedCaseStudies;
      case 'shots':
        return unifiedShots;
      default:
        return unifiedItems;
    }
  }, [unifiedItems, unifiedCaseStudies, unifiedShots, activeFilter]);

  // Sort items based on sort order
  const sortedItems = React.useMemo(() => {
    const items = [...filteredItems];
    
    switch (sortOrder) {
      case 'alphabetical':
        return items.sort((a, b) => a.title.localeCompare(b.title));
      case 'featured_first':
        return items.sort((a, b) => {
          if (a.is_featured && !b.is_featured) return -1;
          if (!a.is_featured && b.is_featured) return 1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
      case 'most_viewed':
        // NOW ACTUALLY SORT BY VIEW COUNT
        return items.sort((a, b) => {
          const viewsA = a.view_count || 0;
          const viewsB = b.view_count || 0;
          if (viewsB !== viewsA) return viewsB - viewsA;
          // Fallback to created_at for ties
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
      case 'last_edited':
        return items.sort((a, b) => {
          const updatedA = new Date(a.updated_at || a.created_at).getTime();
          const updatedB = new Date(b.updated_at || b.created_at).getTime();
          return updatedB - updatedA;
        });
      case 'last_added':
      default:
        return items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
  }, [filteredItems, sortOrder]);


  // Convert unified shots to the format expected by usePortfolioSection
  const unifiedPortfolioItems = React.useMemo(() => {
    return unifiedShots.map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      image_url: item.image_url || '',
      images: item.images || [],
      created_at: item.created_at,
      updated_at: item.updated_at,
      is_featured: item.is_featured,
      status: item.status,
      order: item.order,
      slug: item.slug
    }));
  }, [unifiedShots]);

  const {
    isAdding,
    isEditing,
    handleAddItem: handleAddItemOld,
    handleUpdateItem,
    handleRemoveItem: handleRemoveItemOld,
    handleRenameItem: handleRenameItemOld,
    handleReorderItems: handleReorderItemsOld,
    handleSetFeaturedItem: handleSetFeaturedItemOld,
    handleAddFirstProject,
    handleCancelAdd,
    handleEditItem,
    handleCancelEdit,
    setIsAdding,
    resetUIState,
    isProcessing,
    handleUpdateStatus: handleUpdateStatusOld,
  } = usePortfolioSection({
    initialItems: unifiedPortfolioItems,
    onPortfolioChange,
    sectionId: activeSection,
    isVerified,
  });

  // Use unified portfolio handlers
  const useUnifiedView = true;
  
  // Helper function to convert raw items to unified format
  const convertToUnifiedFormat = (items: typeof unifiedItems) => {
    return items.map(item => {
      if (item.item_type === 'shot') {
        // For shots: use legacy_image_url (old image_url field) or first image from images array
        const coverImage = item.legacy_image_url || (item.images?.[0]?.url || '');
        return {
          id: item.id,
          type: 'shot' as const,
          title: item.title,
          description: item.description,
          cover_image: coverImage,
          created_at: item.created_at,
          updated_at: item.updated_at,
          is_featured: item.is_featured,
          status: item.status,
          order: item.order,
          slug: item.slug,
          image_url: item.legacy_image_url,
          images: item.images,
          profile_id: item.profile_id,
        };
      } else {
        return {
          id: item.id,
          type: 'case_study' as const,
          title: item.title,
          description: item.description,
          cover_image: item.image_url || '',
          created_at: item.created_at,
          updated_at: item.updated_at,
          is_featured: item.is_featured,
          status: item.status,
          order: item.order,
          slug: item.slug,
          seo_title: item.seo_title,
          seo_description: item.seo_description,
          og_image: item.og_image,
          profile_id: item.profile_id,
        };
      }
    });
  };

  // Convert sorted items to proper unified format (for dashboard tabs with sorting)
  const convertedSortedItems = React.useMemo(() => {
    return convertToUnifiedFormat(sortedItems);
  }, [sortedItems]);

  // Convert items for Portfolio Layout (uses database order, not dashboard sorting)
  const portfolioLayoutItems = React.useMemo(() => {
    console.log('📊 Recomputing portfolioLayoutItems, unifiedItems:', unifiedItems.length);

    // Filter to only show published items
    const publishedItems = unifiedItems.filter(item => item.status === 'published');

    // Sort by: 1st featured, then by order
    const orderedItems = [...publishedItems].sort((a, b) => {
      // 1. Featured items first
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;

      // 2. Sort by order field
      const orderA = a.order ?? 999999;
      const orderB = b.order ?? 999999;
      if (orderA !== orderB) return orderA - orderB;

      // 3. Finally by created_at (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    const converted = convertToUnifiedFormat(orderedItems);
    console.log('📊 Converted items orders:', converted.map(i => ({ id: i.id.substring(0, 8), order: i.order, title: i.title.substring(0, 20) })));

    return converted;
  }, [unifiedItems]);

  // Choose data source (unified approach) - use sorted items
  const currentPortfolioItems = convertedSortedItems.filter(item => item.type === 'shot').map(item => ({
    id: item.id,
    title: item.title,
    description: item.description,
    image_url: item.image_url || '',
    images: item.images || [],
    created_at: item.created_at,
    updated_at: item.updated_at,
    is_featured: item.is_featured,
    status: item.status,
    order: item.order,
    slug: item.slug
  }));
  const currentCaseStudies = convertedSortedItems.filter(item => item.type === 'case_study').map(item => ({
    id: item.id,
    title: item.title,
    description: item.description,
    cover_image: item.cover_image || '',
    status: item.status,
    is_featured: item.is_featured,
    slug: item.slug,
    seo_title: item.seo_title,
    seo_description: item.seo_description,
    og_image: item.og_image,
    order_index: item.order || 0,
    created_at: item.created_at,
    updated_at: item.updated_at,
    profile_id: item.profile_id,
    sections: [] // Will be populated separately if needed
  }));
  const currentLoading = unifiedLoading;
  
  const {
    handleRemove: handleUnifiedRemove,
    handleRename: handleUnifiedRename,
    handleReorder: handleUnifiedReorder,
    handleSetFeatured: handleUnifiedSetFeatured,
    handleUpdateStatus: handleUnifiedUpdateStatus,
  } = useUnifiedPortfolio({
    portfolioItems: currentPortfolioItems,
    caseStudies: currentCaseStudies,
    onPortfolioChange,
  });

  // Use unified handlers
  const handleAddItem = handleAddItemOld; // Keep using old handler for adding items
  const handleRemoveItem = handleUnifiedRemove;
  const handleRenameItem = handleUnifiedRename;
  const handleReorderItems = handleUnifiedReorder;
  const handleSetFeaturedItem = handleUnifiedSetFeatured;
  const handleUpdateStatus = handleUnifiedUpdateStatus;

  // Duplicate handler for case studies
  const handleDuplicateItem = React.useCallback(async (id: string) => {
    const item = unifiedItems.find(i => i.id === id);
    if (!item) return;

    try {
      if (item.item_type === "shot") {
        toast.error("Shot duplication is not yet implemented");
      } else if (item.item_type === "case_study") {
        const { CaseStudyService } = await import("@/services/supabase/caseStudies");
        await CaseStudyService.duplicateCaseStudy(id);

        await queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
        await queryClient.invalidateQueries({ queryKey: ['case-studies'] });
        await queryClient.invalidateQueries({ queryKey: ['unified-case-studies'] });

        toast.success("Case study duplicated successfully");
      }
    } catch (error) {
      console.error('Error duplicating item:', error);
      toast.error("Failed to duplicate item");
    }
  }, [unifiedItems, queryClient]);

  // Portfolio Layout specific handler for reordering
  const handlePortfolioLayoutReorder = React.useCallback(async (reorderedItems: any[]) => {
    const queryKey = ['unified-portfolio', profile?.id, true];

    try {
      console.log('Reordering portfolio items:', reorderedItems);

      // Create batch updates with new order values
      const updates = reorderedItems.map((item, index) => ({
        id: item.id,
        type: item.type as 'shot' | 'case_study', // Use 'type' from converted items
        order: index
      }));

      console.log('Batch updates:', updates);

      // OPTIMISTIC UPDATE: Update the cache immediately before the API call
      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData) return oldData;

        // Map the reordered items back to the raw format with updated order
        return oldData.map((item: any) => {
          const update = updates.find(u => u.id === item.id);
          if (update) {
            return { ...item, order: update.order };
          }
          return item;
        });
      });

      // Update the database in the background (don't await to keep UI responsive)
      UnifiedPortfolioService.batchUpdatePortfolioOrder(updates)
        .then(() => {
          console.log('Portfolio order updated successfully');
        })
        .catch((error) => {
          console.error('Error saving portfolio order:', error);
          // On error, revert to server state
          queryClient.invalidateQueries({ queryKey });
        });

    } catch (error) {
      console.error('Error reordering portfolio layout:', error);
      // On error, invalidate to revert to server state
      await queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient, profile?.id]);

  const [pendingImages, setPendingImages] = React.useState<string[] | null>(null);
  const imageUploadRef = React.useRef<{ openFilePicker: () => void }>(null);
  const emptyStateRef = React.useRef<PortfolioEmptyStateHandle>(null);
  const addFormRef = useRef(null);
  const editFormRef = useRef(null);

  const hasItems = unifiedPortfolioItems.length > 0;
  const maxProjectsReached = !isVerified && unifiedPortfolioItems.length >= 6;
  const editingItem = isEditing
    ? unifiedPortfolioItems.find((item) => item.id === isEditing)
    : undefined;
  const showList = !isAdding && !isEditing; // Show list when not adding/editing, regardless of portfolio items
  const hasCaseStudies = unifiedCaseStudies.length > 0;
  const showEmptyState = !hasItems && !hasCaseStudies && !isAdding && !isEditing;

  
  // Show loading state when data is loading
  // Only show if we don't have any cached data
  if (unifiedLoading && unifiedItems.length === 0) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-square bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const handleGenerateBothClick = () => {
    if (isAdding && addFormRef.current) {
      addFormRef.current.generateTitleAndDescription();
    } else if (isEditing && editFormRef.current) {
      editFormRef.current.generateTitleAndDescription();
    }
  };

  // Case Study and Shot Handlers
  const handleSelectShot = () => {
    // Navigate to shot editor page instead of opening modal
    navigate("/dashboard/portfolio/shot/new");
  };

  const handleSelectCaseStudy = () => {
    // Open case study creation modal
    setShowCaseStudyModal(true);
  };

  const handleEditCaseStudy = (caseStudy: CaseStudyItem) => {
    // Navigate to edit case study page
    navigate(`/dashboard/portfolio/case-study/${caseStudy.id}`);
  };

  const handlePreviewCaseStudy = (caseStudy: CaseStudyItem) => {
    navigate(`/case-study/preview/${caseStudy.id}`);
  };

  const handleCreateNewCaseStudy = () => {
    navigate("/dashboard/portfolio/case-study/new");
  };

  // Shot handlers
  const handleEditShot = (shot: PortfolioItem) => {
    navigate(`/dashboard/portfolio/shot/${shot.id}`);
  };

  // Combined edit handler that routes to appropriate editor
  const handleUnifiedEditItem = (id: string) => {
    // Find the item in the unified items to determine type
    const item = convertedSortedItems.find(i => i.id === id);
    if (item?.type === "shot") {
      // Convert UnifiedPortfolioItem to PortfolioItem format
      const shot = {
        id: item.id,
        title: item.title,
        description: item.description,
        image_url: item.image_url || '',
        images: item.images || [],
        created_at: item.created_at,
        updated_at: item.updated_at,
        is_featured: item.is_featured,
        status: item.status,
        order: item.order,
        slug: item.slug
      };
      handleEditShot(shot);
    } else if (item?.type === "case_study") {
      // Convert UnifiedPortfolioItem to CaseStudyItem format
      const caseStudy = {
        id: item.id,
        title: item.title,
        description: item.description,
        seo_title: item.seo_title || '',
        seo_description: item.seo_description || '',
        og_image: item.og_image || '',
        status: item.status,
        is_featured: item.is_featured,
        order: item.order,
        created_at: item.created_at,
        updated_at: item.updated_at,
        slug: item.slug,
        sections: [] // Will be populated separately if needed
      };
      handleEditCaseStudy(caseStudy);
    }
  };

  let rightAction = null;
  if ((isAdding || isEditing)) {
    // Only show if there are images in the form (let the form handle the check and toast)
    rightAction = (
      <Button
        type="button"
        onClick={handleGenerateBothClick}
        variant="outline"
        className="flex items-center gap-2 border border-gray-200 bg-white text-black hover:border-transparent hover:text-white hover:[&_svg]:text-white hover:bg-orange-500"
      >
        <Sparkles className="h-4 w-4 mr-2" />
        Generate Title & Description
      </Button>
    );
  }

  return (
    <div className="space-y-4">
      <PortfolioHeader
        hasItems={hasItems}
        isAdding={isAdding}
        isEditing={Boolean(isEditing)}
        onAddClick={() => setShowUploader(!showUploader)}
        onBackClick={isAdding ? handleCancelAdd : handleCancelEdit}
        maxReached={maxProjectsReached}
        isProcessing={isProcessing}
        rightAction={rightAction}
        onSelectShot={handleSelectShot}
        onSelectCaseStudy={handleSelectCaseStudy}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        sortOrder={sortOrder}
        onSortChange={setSortOrder}
        allCount={allCount}
        caseStudiesCount={caseStudiesCount}
        shotsCount={shotsCount}
        saveStatus={shotSaveStatus}
        showAutosaveIndicator={isAdding || Boolean(isEditing)}
      />

      {/* Combined grid - tabs removed */}

      {showUploader && hasItems && !isAdding && !isEditing && (
        <ImageAddingSection
          ref={imageUploadRef}
          isAddingImage={true}
          hasImages={hasItems}
          onAddImage={(urls) => {
            setPendingImages(urls);
            setIsAdding(true);
            setShowUploader(false);
          }}
          onCancelAdd={() => setShowUploader(false)}
          onAddClick={() => imageUploadRef.current?.openFilePicker()}
          variant="default"
        />
      )}

      {showList && activeFilter === 'portfolio_layout' && (
        <div className="space-y-4">
          <PortfolioLayoutList
            items={portfolioLayoutItems}
            onRemove={handleRemoveItem}
            onEdit={handleUnifiedEditItem}
            onRename={handleRenameItem}
            onSetFeatured={handleSetFeaturedItem}
            onUpdateStatus={handleUpdateStatus}
            onDuplicate={handleDuplicateItem}
            onReorder={handlePortfolioLayoutReorder}
            sortOrder={sortOrder}
            isProcessing={isProcessing}
          />
        </div>
      )}

      {showList && activeFilter !== 'portfolio_layout' && (
        <CombinedPortfolioList
          portfolioItems={currentPortfolioItems}
          caseStudies={currentCaseStudies}
          caseStudiesLoading={currentLoading}
          onRemove={handleRemoveItem}
          onEdit={handleUnifiedEditItem}
          onRename={handleRenameItem}
          onReorder={handleReorderItems}
          onSetFeatured={handleSetFeaturedItem}
          onUpdateStatus={handleUpdateStatus}
          onEditCaseStudy={handleEditCaseStudy}
          onPreviewCaseStudy={handlePreviewCaseStudy}
          onCreateNewCaseStudy={handleCreateNewCaseStudy}
          onPortfolioChange={onPortfolioChange}
          isProcessing={isProcessing}
          useUnifiedView={true} // Enable unified view
          unifiedItems={convertedSortedItems} // Pass converted and sorted items
          useUnifiedSource={true} // Use unified source
          sortOrder={sortOrder}
        />
      )}


      {showEmptyState && (
        <PortfolioEmptyState
          ref={emptyStateRef}
          onImagesUploaded={(urls) => {
            setPendingImages(urls);
            setIsAdding(true);
          }}
        />
      )}

      {isEditing && editingItem && (
        <EditPortfolioItemForm
          ref={editFormRef}
          item={editingItem}
          onUpdateItem={(updatedItem) => {
            handleUpdateItem(updatedItem);
            handleCancelEdit();
            resetUIState();
          }}
          onCancel={handleCancelEdit}
          userProfession={userProfession}
          userSkills={userSkills}
          onSaveStatusChange={setShotSaveStatus}
        />
      )}

      {isAdding && !isEditing && (
        <AddPortfolioItemForm
          ref={addFormRef}
          onAddItem={(newItem) => {
            handleAddItem(newItem);
            handleCancelAdd();
            resetUIState();
            setPendingImages(null);
            setImportedShotData(null);
            setShotSaveStatus('idle');
          }}
          onCancel={() => {
            handleCancelAdd();
            setPendingImages(null);
            setImportedShotData(null);
            setShotSaveStatus('idle');
          }}
          userProfession={userProfession}
          userSkills={userSkills}
          initialImages={pendingImages || undefined}
          initialData={importedShotData || undefined}
          onSaveStatusChange={setShotSaveStatus}
        />
      )}


      {/* Case Study Creation Modal */}
      <CaseStudyCreationModal
        open={showCaseStudyModal}
        onClose={() => setShowCaseStudyModal(false)}
        onStartFromScratch={() => {
          setShowCaseStudyModal(false);
          navigate("/dashboard/portfolio/case-study/new");
        }}
        onStartWithImportedData={(data) => {
          console.log("[PortfolioSection] Received imported case study data:", data);
          setShowCaseStudyModal(false);
          // Navigate to new case study page with imported data
          // You can pass the data via navigation state
          navigate("/dashboard/portfolio/case-study/new", {
            state: { importedData: data }
          });
        }}
      />

      {/* Shot Creation Modal */}
      <ShotCreationModal
        open={showShotModal}
        onClose={() => setShowShotModal(false)}
        onStartFromScratch={() => {
          setShowShotModal(false);
          setIsAdding(true);
        }}
        onStartWithImportedData={(data) => {
          console.log("[PortfolioSection] Received imported shot data:", data);
          setShowShotModal(false);
          // Store imported data (title, description, etc.) and extract images
          setImportedShotData(data);
          const importedImages = data.images?.map(img => img.url) || [];
          setPendingImages(importedImages);
          setIsAdding(true);
        }}
      />
    </div>
  );
};

export default React.memo(PortfolioSection);
